<style lang="scss">
.progress {
    display: inline-block;
    border-radius: 100%;
    position: relative;
    color: #fff;
    background: rgba(0, 0, 0, 0.4);

    &.spin {
        .main {
            animation: spin 4s linear infinite;
        }
    }

    &.indeterminate {
        circle {
            transition: none;
        }
    }
}

svg {
    vertical-align: top;
}

.cancel {
    $size: 12px;
    position: absolute;
    top: 50%;
    left: 50%;
    margin: -($size / 2);
    height: $size;
    width: $size;
}

circle {
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    transform: rotate(-90deg);
    transform-origin: 50% 50%;
    transition: stroke-dasharray 0.3s;
}

path {
    fill: currentColor;
}

@keyframes spin {
    from {
        transform: rotate(0);
    }

    from {
        transform: rotate(-1turn);
    }
}

</style>
<script lang="ts">
import { onDestroy } from 'svelte';

export let size = 32;
export let radius = 12;
export let thickness = 2;
export let progress = 0;
/** Минимальное значение прогресса: чтобы не показывать пустой круг */
export let min = 0.03;
export let cancelable = false;
export let spin = false;
/** Состояние, при котором неизвестен прогресс */
export let indeterminate = false;

const pi2 = Math.PI * 2;
const indeterminateDuration = 1400;
const maxIndeterminate = 0.7;
let raf: number;
let css = '';

$: css = drawProgress(Math.max(progress || 0, min));
$: indeterminate ? startIndeterminate() : stopIndeterminate();

onDestroy(stopIndeterminate);

function easeInOut(t: number, b: number, c: number, d: number) {
    return (t /=d / 2) < 1
        ? c / 2 * t * t * t + b
        : c / 2 * ((t -= 2) * t * t + 2) + b;
}

/**
 * Запуск анимации с неизвестным прогрессом
 */
function startIndeterminate() {
    let start = performance.now();
    const loop = (now: number) => {
        const time = (now - start) % indeterminateDuration;
        const pos = easeInOut(time, 0, 1, indeterminateDuration) * 2;
        const progressValue = pos < 1 ? pos : 2 - pos;
        const value = min + (progressValue * (maxIndeterminate - min));
        const offset = -Math.max(pos - 1, 0);

        css = drawProgress(value, offset);
        raf = requestAnimationFrame(loop);
    };

    loop(start);
}

/**
 * Остановка анимации с неизвестным прогрессом
 */
function stopIndeterminate() {
    cancelAnimationFrame(raf);
}

/**
 * Отрисовка прогресса
 * @param value Текущее значение прогресса (от 0 до 1)
 * @param offset Смещение позиции рисования прогресса (от 0 до 1)
 * @returns CSS с описанием прогресса
 */
function drawProgress(value: number, offset = 0): string {
    const len = radius * pi2;
    const progress = value * len;
    const dashArray = `${progress}, ${len - progress}`;
    const dashOffset = offset * len;
    return value
        ? `stroke-dasharray: ${dashArray}; stroke-dashoffset: ${dashOffset}`
        : 'stroke: none';
}
</script>

<span class="progress" class:spin class:indeterminate on:click>
    <svg class="main" width={size} height={size} viewBox="0 0 {size} {size}" version="1.1" xmlns="http://www.w3.org/2000/svg">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke-width={thickness} style={css}></circle>
        <circle cx={size / 2} cy={size / 2} r={radius} stroke-width={thickness} opacity=".1"></circle>
    </svg>
    {#if cancelable}
        <svg class="cancel" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" >
            <path fill-rule="evenodd" d="M6.84 8L3.25 4.4a.8.8 0 1 1 1.13-1.13l3.6 3.59 3.58-3.6a.8.8 0 0 1 1.13 1.14L9.1 8l3.59 3.58a.8.8 0 0 1-1.13 1.14l-3.59-3.6-3.59 3.6a.8.8 0 0 1-1.13-1.14L6.84 8z"/>
        </svg>
    {/if}
</span>
